// 通用视频播放器（HFLV / HLS 双协议）
// - 调 callInternal('/play/weburl') 拿 play_url
// - 自动选 flv / hls 协议（按 URL 后缀或 prop）
// - 支持外部直接传 url（公开测试流场景）
// - imperative API: load(url, protocol) / stop() （via ref）

import { Card, Tag, Alert, Button, Space, Typography } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import Hls from 'hls.js';
import { callInternal } from '../api/internal.js';
import { msePlayer, type MsePlayerInstance } from '../utils/mse-player.js';
import { apiUrl, isCapacitor } from '../utils/api-base.js';

export type VideoProtocol = 'flv' | 'hls';

// 1×1 透明 GIF。设了 poster 后，Android WebView 不再画原生灰色「无内容」播放占位 UI，
// 让 video 元素的 background:#000 直接透出，呈现纯黑（与 desktop 浏览器表现一致）。
const BLANK_VIDEO_POSTER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

// HTTPS 页面 / Capacitor 包壳下，上游 http:// 流会被拦截：
// - 浏览器：mixed content 拦
// - Capacitor：默认 cleartext blocked + file:// 跨域
// 包一层后端 /api/stream 代理（同源 https）解决。
function maybeProxyStream(url: string): string {
	if (typeof window === 'undefined') return url;
	if (!url.startsWith('http://')) return url;
	// HTTPS 页面 → 必须代理；Capacitor app → 也代理（避免 cleartext + 跨域）
	if (window.location.protocol !== 'https:' && !isCapacitor()) return url;
	return apiUrl(`/api/stream?url=${encodeURIComponent(url)}`);
}

export interface UluVideoPlayerHandle {
	/** 加载播放任意 URL（公开测试流 / weburl 返回的 play_url） */
	loadUrl: (url: string, protocol: VideoProtocol, label?: string) => void;
	/** 加载 ulucu 设备实时画面（调 /play/weburl） */
	loadDevice: (deviceSn: string, channelIdx: number, label?: string) => Promise<void>;
	stop: () => void;
}

interface UluVideoPlayerProps {
	/** 初始加载（可选） */
	initialUrl?: string;
	initialProtocol?: VideoProtocol;
	/** 标题前缀（默认空） */
	titlePrefix?: string;
	/** 高度（视频容器） */
	height?: number;
	/** 显示头部 tag bar（idle/playing/error + lib supported） */
	showHeaderTags?: boolean;
	/** 显示日志面板 */
	showLogs?: boolean;
	/** Chrome-less 模式：去掉 Card/标题/控制条/日志 — 仅返回 <video>。供 m-tabs/Player 完整复刻外壳用 */
	bareMode?: boolean;
	/** 状态变化回调（chrome-less 时用于外壳同步） */
	onStatusChange?: (status: 'idle' | 'loading' | 'playing' | 'error', err?: string | null) => void;
}

interface WebUrlResponse {
	play_url: string;
	source?: string;
	domain_list?: string;
}

function detectProtocol(url: string): VideoProtocol {
	if (url.includes('.flv') || url.includes('/live/') && !url.includes('.m3u8')) return 'flv';
	return 'hls';
}

const UluVideoPlayer = forwardRef<UluVideoPlayerHandle, UluVideoPlayerProps>(({
	initialUrl,
	initialProtocol,
	titlePrefix = '',
	height = 360,
	showHeaderTags = true,
	showLogs = true,
	bareMode = false,
	onStatusChange,
}, ref) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const flvRef = useRef<MsePlayerInstance | null>(null);
	const hlsRef = useRef<Hls | null>(null);
	const heartbeatRef = useRef<number | null>(null);     // 媒体心跳定时器（30s）
	const sourceRef = useRef<string | null>(null);        // device_id-channel_idx-rate（心跳参数）
	const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [logs, setLogs] = useState<string[]>([]);
	const [label, setLabel] = useState<string>('');

	const log = (msg: string) => {
		// 同时输出到 console，方便在 Safari Web Inspector / 桌面 DevTools 排查
		console.log(`[ulucu-player] ${msg}`);
		setLogs((prev) => [`${new Date().toLocaleTimeString()}  ${msg}`, ...prev].slice(0, 12));
	};

	const stopHeartbeat = () => {
		if (heartbeatRef.current != null) {
			clearInterval(heartbeatRef.current);
			heartbeatRef.current = null;
		}
		sourceRef.current = null;
	};

	/**
	 * 媒体心跳：开放平台文档要求每 30 秒发一次保活，否则 play_url 失效。
	 * internal API 同名路径 /play/media_heartbeat 也可用（实测 code=10004
	 * find session failed 表明接口路径有效，只需有真实 session 就 code=0）。
	 * 参数：type=1(实时)/2(回放), play_type=hflv|hls|..., source=<sn>-<ch>-<rate>
	 */
	const startHeartbeat = (source: string, playType: 'hflv' | 'hls') => {
		stopHeartbeat();
		sourceRef.current = source;
		const tick = async () => {
			if (!sourceRef.current) return;
			try {
				await callInternal({
					short_name: 'base',
					action: '/play/media_heartbeat',
					method: 'GET',
					query: { type: '1', play_type: playType, source: sourceRef.current },
					av: '1',
				});
			} catch {
				/* 静默 — 心跳偶尔失败不该中断播放 */
			}
		};
		// 30 秒间隔 — 文档明确要求
		heartbeatRef.current = window.setInterval(tick, 30_000);
		// 立即先发一次以确认 session
		void tick();
	};

	const stop = () => {
		stopHeartbeat();
		if (flvRef.current) {
			try {
				flvRef.current.pause();
				flvRef.current.unload();
				flvRef.current.detachMediaElement();
				flvRef.current.destroy();
			} catch {}
			flvRef.current = null;
		}
		if (hlsRef.current) {
			try { hlsRef.current.destroy(); } catch {}
			hlsRef.current = null;
		}
		if (videoRef.current) videoRef.current.src = '';
		setStatus('idle');
	};

	const playFlv = (url: string) => {
		if (!msePlayer.isSupported()) {
			setStatus('error');
			setErrorMsg('MSE not supported');
			log('✗ msePlayer.isSupported() = false');
			return;
		}
		const player = msePlayer.createPlayer({
			type: 'flv', url, isLive: true, cors: true,
		}, { enableWorker: false, enableStashBuffer: false, stashInitialSize: 128 });
		flvRef.current = player;
		const video = videoRef.current!;
		video.muted = true;  // iOS autoplay 必需，防 NotAllowedError
		player.attachMediaElement(video);
		player.on(msePlayer.Events.MEDIA_INFO, ((...args: unknown[]) => {
			const info = args[0] as { width?: number; height?: number; fps?: number };
			log(`✓ FLV ${info.width}x${info.height} @ ${info.fps}fps`);
			setStatus('playing');
		}));
		// fallback：MEDIA_INFO 偶尔不触发（某些 ulucu 流），用 video 'playing' 事件兜底
		const onPlaying = () => {
			log(`✓ video.playing event fired (fallback)`);
			setStatus('playing');
		};
		video.addEventListener('playing', onPlaying, { once: true });
		player.on(msePlayer.Events.ERROR, ((...args: unknown[]) => {
			const errorType = args[0] as string;
			const errorDetail = args[1] as string;
			// 这些错误是「流正常断开/回放结束/网络抖动」类的，不是真正的故障，静默 log 不显示
			const benign = /UnrecoverableEarlyEof|NetworkStatusCodeInvalid|EARLY_EOF|MediaSourceEnded/i;
			if (benign.test(errorType) || benign.test(errorDetail)) {
				log(`⊘ FLV stream ended: ${errorType} / ${errorDetail}`);
				setStatus('idle');
				return;
			}
			log(`✗ FLV ERROR: ${errorType} / ${errorDetail}`);
			setStatus('error');
			setErrorMsg(`${errorType}: ${errorDetail}`);
		}));
		player.load();
		const ret = player.play();
		if (ret && typeof (ret as Promise<void>).then === 'function') {
			(ret as Promise<void>).catch((e: Error) => {
				const msg = e?.message ?? String(e);
				// Strict Mode 双 mount 或快速切设备时 play() 会被新 load 中断，非致命，忽略
				if (/interrupted by a new load|aborted/i.test(msg)) {
					log(`⊘ play() superseded: ${msg.slice(0, 60)}`);
					return;
				}
				log(`✗ play() rejected: ${msg}`);
				setStatus('error');
				setErrorMsg(msg);
			});
		}
	};

	const playHls = (url: string) => {
		const video = videoRef.current!;
		video.muted = true;  // iOS autoplay 必需，防 NotAllowedError
		// Safari / Chrome 147+ 原生支持 HLS
		if (video.canPlayType('application/vnd.apple.mpegurl')) {
			log(`→ HLS native, UA=${navigator.userAgent.slice(0, 100)}`);
			// iOS 原生 HLS 静默错误：video.error 事件 + networkState/readyState 才能看出
			const onError = () => {
				const e = video.error;
				const codeName = ['', 'ABORTED', 'NETWORK', 'DECODE', 'SRC_NOT_SUPPORTED'][e?.code ?? 0] || '?';
				const msg = `video.error code=${e?.code} (${codeName}) msg="${e?.message || ''}" net=${video.networkState} ready=${video.readyState}`;
				log(`✗ native HLS ${msg}`);
				setStatus('error');
				setErrorMsg(msg);
			};
			const onStalled = () => log(`⚠ video stalled (net=${video.networkState} ready=${video.readyState})`);
			const onSuspend = () => log(`⚠ video suspend (net=${video.networkState} ready=${video.readyState})`);
			const onCanPlay = () => log(`✓ video canplay (${video.videoWidth}x${video.videoHeight})`);
			const onPlaying = () => { log(`✓ video playing`); setStatus('playing'); };
			video.addEventListener('error', onError, { once: true });
			video.addEventListener('stalled', onStalled);
			video.addEventListener('suspend', onSuspend);
			video.addEventListener('canplay', onCanPlay, { once: true });
			video.addEventListener('playing', onPlaying, { once: true });
			video.src = url;
			void video.play().catch((e) => {
				log(`✗ native HLS play() rejected: ${e?.message ?? e}`);
				// 不直接 setStatus('error')；很多情况下 play() reject 后 video 仍能播
			});
			video.addEventListener('loadedmetadata', () => {
				log(`✓ HLS metadata: ${video.videoWidth}x${video.videoHeight} dur=${video.duration}`);
			}, { once: true });
			return;
		}
		if (!Hls.isSupported()) {
			setStatus('error');
			setErrorMsg('hls.js not supported');
			log('✗ Hls.isSupported() = false');
			return;
		}
		const hls = new Hls({ debug: false });
		hlsRef.current = hls;
		hls.on(Hls.Events.MANIFEST_PARSED, (_: unknown, data: { levels?: { width?: number; height?: number }[] }) => {
			log(`✓ HLS ${data.levels?.[0]?.width}x${data.levels?.[0]?.height}`);
			setStatus('playing');
			void video.play().catch(() => {});
		});
		hls.on(Hls.Events.ERROR, (_: unknown, data: { type?: string; details?: string; fatal?: boolean }) => {
			if (data.fatal) {
				log(`✗ HLS FATAL: ${data.type} / ${data.details}`);
				setStatus('error');
				setErrorMsg(`${data.type}: ${data.details}`);
			}
		});
		hls.loadSource(url);
		hls.attachMedia(video);
	};

	const loadUrl = (url: string, protocol: VideoProtocol, lbl?: string) => {
		stop();
		setLabel(lbl ?? '');
		setStatus('loading');
		setErrorMsg(null);
		log(`→ ${protocol.toUpperCase()} load: ${url.slice(0, 80)}${url.length > 80 ? '...' : ''}`);
		setTimeout(() => {
			try {
				if (protocol === 'flv') playFlv(url);
				else playHls(url);
			} catch (e) {
				setStatus('error');
				setErrorMsg((e as Error).message);
				log(`✗ exception: ${(e as Error).message}`);
			}
		}, 50);
	};

	const loadDevice = async (deviceSn: string, channelIdx: number, lbl?: string) => {
		stop();
		setLabel(lbl ?? deviceSn);
		setStatus('loading');
		setErrorMsg(null);
		const rate = 1000;
		// MSE 支持检测：桌面用 flv.js，iOS 17.1+ 用 mpegts.js（ManagedMediaSource）。
		// 不支持 MSE 时降级到 HLS（iOS 原生 .m3u8 / 桌面 hls.js）。
		// 注意：ulucu 多数设备 srs 节点只发 hflv 不发 hls，hls 路径多半 404，能 hflv 就 hflv。
		const useFlv = msePlayer.isSupported();
		const playType: 'hflv' | 'hls' = useFlv ? 'hflv' : 'hls';
		log(`→ /play/weburl: ${deviceSn} ch=${channelIdx} (${playType.toUpperCase()})`);
		try {
			const res = await callInternal<WebUrlResponse>({
				short_name: 'base',
				action: '/play/weburl',
				query: { device_id: deviceSn, channel_idx: channelIdx, rate, play_type: playType },
				av: '1',
			});
			// 容错：上游偶尔返回 code=1005「domain_list config error」但 data.play_url
			// 仍然有效（实测）。只要有 play_url 就尝试播放，flv.js / hls.js 会自己判定能否解流。
			if (!res.data?.play_url) {
				setStatus('error');
				setErrorMsg(`code=${res.code} msg=${res.msg}`);
				log(`✗ weburl failed: ${res.msg}`);
				return;
			}
			if (res.code !== 0) {
				log(`⚠ weburl code=${res.code} msg=${res.msg}（仍尝试播放，play_url 已返回）`);
			}
			const finalUrl = maybeProxyStream(res.data.play_url);
			log(`✓ play_url got${finalUrl !== res.data.play_url ? '（已经过 /api/stream 代理）' : ''}, starting ${useFlv ? 'flv.js' : 'HLS'}...`);
			if (useFlv) playFlv(finalUrl);
			else playHls(finalUrl);
			// 启动心跳保活（source 优先用上游返回，其次用 sn-ch-rate 拼）
			const source = (res.data as { source?: string }).source || `${deviceSn}-${channelIdx}-${rate}`;
			startHeartbeat(source, playType);
		} catch (e) {
			setStatus('error');
			setErrorMsg((e as Error).message);
		}
	};

	useImperativeHandle(ref, () => ({ loadUrl, loadDevice, stop }));

	useEffect(() => {
		onStatusChange?.(status, errorMsg);
	}, [status, errorMsg, onStatusChange]);

	useEffect(() => {
		if (initialUrl) {
			loadUrl(initialUrl, initialProtocol ?? detectProtocol(initialUrl));
		}
		return () => stop();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (bareMode) {
		return (
			<video
				ref={videoRef}
				autoPlay
				playsInline
				muted
				poster={BLANK_VIDEO_POSTER}
				style={{ width: '100%', height: '100%', display: 'block', background: '#000', objectFit: 'contain' }}
			/>
		);
	}

	const flvSupported = msePlayer.isSupported();
	const hlsSupported = Hls.isSupported() || !!videoRef.current?.canPlayType('application/vnd.apple.mpegurl');

	return (
		<Card
			title={
				showHeaderTags ? (
					<Space wrap>
						<span>{label ? `${titlePrefix}📹 ${label}` : (titlePrefix || 'Video')}</span>
						<Tag color={status === 'playing' ? 'green' : status === 'error' ? 'red' : status === 'loading' ? 'blue' : 'default'}>
							{status}
						</Tag>
						<Tag color={flvSupported ? 'green' : 'red'}>flv.js {flvSupported ? '✓' : '✗'}</Tag>
						<Tag color={hlsSupported ? 'green' : 'red'}>hls.js {hlsSupported ? '✓' : '✗'}</Tag>
					</Space>
				) : (label || titlePrefix)
			}
			styles={{ body: { padding: 12 } }}
		>
			<Space direction="vertical" style={{ width: '100%' }} size="small">
				<div style={{ background: '#000', borderRadius: 8, overflow: 'hidden', width: '100%', maxWidth: 720 }}>
					<video
						ref={videoRef}
						controls
						autoPlay
						playsInline
						muted
						poster={BLANK_VIDEO_POSTER}
						style={{ width: '100%', display: 'block', height, background: '#000', objectFit: 'contain' }}
					/>
				</div>

				<Space>
					<Button icon={<PauseCircleOutlined />} onClick={stop}>停止</Button>
				</Space>

				{errorMsg && <Alert type="error" showIcon message={errorMsg} closable onClose={() => setErrorMsg(null)} />}

				{showLogs && logs.length > 0 && (
					<details>
						<summary style={{ cursor: 'pointer', color: '#999', fontSize: 12 }}>日志 ({logs.length})</summary>
						<pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, fontSize: 11, marginTop: 4 }}>
							{logs.join('\n')}
						</pre>
					</details>
				)}
			</Space>
		</Card>
	);
});
UluVideoPlayer.displayName = 'UluVideoPlayer';
export default UluVideoPlayer;
